import { useState, useMemo } from 'react';
import { ExceedanceBadge } from './ExceedanceBadge';
import { TrendBadge } from './TrendBadge';
import { formatConcentration } from '@/utils/format';
import { getAnalyteSeverity } from '@/utils/format';
import type { WellConcentrationPrediction } from '@/types';

interface WellPredictionsTableProps {
  predictions: WellConcentrationPrediction[];
  analytes: string[];
  selectedAnalyte?: string | null;
  onFilterAnalyte?: (code: string | null) => void;
  isLoading?: boolean;
}

type SortField = 'well_name' | 'analyte_code' | 'current_conc_mgl' | 'predicted_conc_mgl' | 'exceedance_probability' | 'trend';
type SortDir = 'asc' | 'desc';

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-b border-mono-800/50">
          {Array.from({ length: 7 }).map((_, j) => (
            <td key={j} className="px-3 py-2.5">
              <div className="h-3 bg-mono-800 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function WellPredictionsTable({
  predictions,
  analytes,
  selectedAnalyte,
  onFilterAnalyte,
  isLoading = false,
}: WellPredictionsTableProps) {
  const [sortField, setSortField] = useState<SortField>('exceedance_probability');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'exceedance_probability' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    const arr = [...predictions];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'well_name':
          cmp = a.well_name.localeCompare(b.well_name);
          break;
        case 'analyte_code':
          cmp = a.analyte_code.localeCompare(b.analyte_code);
          break;
        case 'current_conc_mgl':
          cmp = (a.current_conc_mgl ?? 0) - (b.current_conc_mgl ?? 0);
          break;
        case 'predicted_conc_mgl':
          cmp = a.predicted_conc_mgl - b.predicted_conc_mgl;
          break;
        case 'exceedance_probability':
          cmp = a.exceedance_probability - b.exceedance_probability;
          // Tie-break by analyte severity (benzene first)
          if (cmp === 0) cmp = getAnalyteSeverity(a.analyte_code) - getAnalyteSeverity(b.analyte_code);
          break;
        case 'trend':
          cmp = a.trend.localeCompare(b.trend);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [predictions, sortField, sortDir]);

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest cursor-pointer hover:text-mono-300 select-none"
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field && (
        <span className="ml-1 text-mono-400">{sortDir === 'asc' ? '▲' : '▼'}</span>
      )}
    </th>
  );

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden">
      {/* Header with filter */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-mono-800">
        <h3 className="text-xs font-semibold text-mono-300 tracking-widest">
          WELL PREDICTIONS
        </h3>
        {analytes.length > 1 && onFilterAnalyte && (
          <select
            value={selectedAnalyte || ''}
            onChange={(e) => onFilterAnalyte(e.target.value || null)}
            className="bg-mono-800 border border-mono-700 text-mono-300 text-[10px] tracking-wider px-2 py-1 rounded focus:outline-none focus:border-mono-500"
          >
            <option value="">ALL ANALYTES</option>
            {analytes.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-mono-800">
              <SortHeader field="well_name" label="WELL" />
              <SortHeader field="analyte_code" label="ANALYTE" />
              <SortHeader field="current_conc_mgl" label="CURRENT (MG/L)" />
              <SortHeader field="predicted_conc_mgl" label="PREDICTED (MG/L)" />
              <th className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest">MCL</th>
              <SortHeader field="exceedance_probability" label="P(EXCEED)" />
              <SortHeader field="trend" label="TREND" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-mono-600 text-xs tracking-widest">
                  NO PREDICTIONS FOUND
                </td>
              </tr>
            ) : (
              sorted.map((pred) => {
                const severity = getAnalyteSeverity(pred.analyte_code);
                const severityBorder = severity === 1
                  ? 'border-l-2 border-l-red-400/30'
                  : severity === 2
                    ? 'border-l-2 border-l-amber-400/20'
                    : '';

                return (
                  <tr
                    key={pred.id}
                    className={`border-b border-mono-800/50 hover:bg-mono-900/30 ${severityBorder}`}
                  >
                    <td className="px-3 py-2.5 text-mono-200 font-medium">
                      {pred.well_name}
                    </td>
                    <td className="px-3 py-2.5 text-mono-400">
                      {pred.analyte_code}
                    </td>
                    <td className={`px-3 py-2.5 font-mono ${pred.current_exceeds_mcl ? 'text-red-400' : 'text-mono-300'}`}>
                      {formatConcentration(pred.current_conc_mgl)}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-mono-300" title={
                      pred.ci_90_lower_mgl != null && pred.ci_90_upper_mgl != null
                        ? `90% CI: ${formatConcentration(pred.ci_90_lower_mgl)} – ${formatConcentration(pred.ci_90_upper_mgl)}`
                        : undefined
                    }>
                      {formatConcentration(pred.predicted_conc_mgl)}
                      {pred.ci_90_lower_mgl != null && pred.ci_90_upper_mgl != null && (
                        <span className="text-mono-600 ml-1 text-[9px]">
                          ±
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-mono-500">
                      {formatConcentration(pred.mcl_mgl)}
                    </td>
                    <td className="px-3 py-2.5">
                      <ExceedanceBadge probability={pred.exceedance_probability} size="sm" />
                    </td>
                    <td className="px-3 py-2.5">
                      <TrendBadge trend={pred.trend} confidence={pred.trend_confidence} size="sm" />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with count */}
      {!isLoading && sorted.length > 0 && (
        <div className="px-4 py-2 border-t border-mono-800 bg-mono-850/50 text-[10px] text-mono-500 tracking-wider">
          {sorted.length} prediction{sorted.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
